// Fase 14A — Admin Language & Translation Management (punti 97-104).
import { useCallback, useEffect, useState } from "react";
import { Languages, CheckCircle2, Archive } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { useI18n } from "@/context/I18nContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const STATUS_STYLES = {
  ACTIVE: "bg-green-50 text-green-800 border-green-300",
  TESTING: "bg-amber-50 text-amber-800 border-amber-300",
  DRAFT: "bg-slate-50 text-slate-700 border-slate-300",
  DISABLED: "bg-red-50 text-red-800 border-red-300",
};
const TR_STATUS_STYLES = {
  ACTIVE: "bg-green-50 text-green-800 border-green-300",
  VERIFIED: "bg-emerald-50 text-emerald-800 border-emerald-300",
  TO_REVIEW: "bg-amber-50 text-amber-800 border-amber-300",
  AI_TRANSLATED: "bg-sky-50 text-sky-800 border-sky-300",
  DRAFT: "bg-slate-50 text-slate-700 border-slate-300",
  DEPRECATED: "bg-red-50 text-red-800 border-red-300",
};

export default function AdminLanguages() {
  const { t } = useI18n();
  const [data, setData] = useState(null);
  const [queue, setQueue] = useState(null);
  const [feedback, setFeedback] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [queueFilter, setQueueFilter] = useState("TO_REVIEW");
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    try {
      const [langs, fb, an] = await Promise.all([
        api.get("/admin/languages"),
        api.get("/admin/help/feedback"),
        api.get("/admin/help/analytics"),
      ]);
      setData(langs.data);
      setFeedback(fb.data.feedback || []);
      setAnalytics(an.data);
      setError(null);
    } catch (e) {
      setError(formatApiError(e));
    }
  }, []);

  const loadQueue = useCallback(async () => {
    try {
      const { data: q } = await api.get("/admin/help/translations",
        { params: { status: queueFilter } });
      setQueue(q);
    } catch (e) {
      toast.error(t("common.error"), { description: formatApiError(e) });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queueFilter]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadQueue(); }, [loadQueue]);

  const setStatus = async (code, status) => {
    try {
      await api.put(`/admin/languages/${code}`, { status });
      toast.success(t("adminLang.statusUpdated"), { description: `${code} → ${status}` });
      await load();
    } catch (e) {
      toast.error(t("common.error"), { description: formatApiError(e) });
    }
  };

  const setTranslationStatus = async (collection, id, status) => {
    try {
      await api.put(`/admin/help/translations/${collection}/${id}`, { status });
      toast.success(t("adminLang.statusUpdated"), { description: status });
      await loadQueue();
      await load();
    } catch (e) {
      toast.error(t("common.error"), { description: formatApiError(e) });
    }
  };

  if (error) return <p className="text-sm text-red-700" data-testid="admin-lang-error">{error}</p>;
  if (!data) return <p className="text-sm text-slate-600" data-testid="admin-lang-loading">{t("common.loading")}</p>;

  const queueItems = queue
    ? Object.entries(queue).flatMap(([coll, items]) =>
        items.map((i) => ({ ...i, _coll: coll })))
    : [];

  return (
    <div className="space-y-6" data-testid="admin-languages-page">
      <div>
        <h1 className="text-2xl md:text-3xl font-bold tracking-tight text-slate-900 flex items-center gap-2">
          <Languages className="w-7 h-7 text-green-800" aria-hidden="true" />
          {t("adminLang.title")}
        </h1>
        <p className="text-sm text-slate-600 mt-1">{t("adminLang.subtitle")}</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {data.languages.map((l) => (
          <Card key={l.code} className="border-slate-200" data-testid={`admin-lang-${l.code}`}>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm flex items-center justify-between gap-2">
                <span>{l.name} <span className="text-slate-400 font-normal">({l.code} · {l.locale})</span></span>
                <Badge variant="outline" className={STATUS_STYLES[l.status]}
                  data-testid={`admin-lang-status-${l.code}`}>{l.status}</Badge>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2 text-xs">
              {Object.entries(l.coverage).map(([area, pct]) => (
                <div key={area} className="flex items-center gap-2">
                  <span className="w-20 text-slate-500 capitalize">{area}</span>
                  <div className="flex-1 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                    <div className="h-full bg-green-700" style={{ width: `${pct}%` }} />
                  </div>
                  <span className="w-9 text-right font-medium">{pct}%</span>
                </div>
              ))}
              <div className="flex items-center justify-between pt-2 border-t border-slate-100">
                <span className="font-semibold text-slate-700">{t("adminLang.coverage")}: {l.overall_coverage}%</span>
                <Select value={l.status} onValueChange={(v) => setStatus(l.code, v)}>
                  <SelectTrigger className="h-7 w-28 text-xs" data-testid={`admin-lang-status-select-${l.code}`}
                    aria-label={`${t("adminLang.status")} ${l.name}`}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {["DRAFT", "TESTING", "ACTIVE", "DISABLED"].map((s) => (
                      <SelectItem key={s} value={s} data-testid={`admin-lang-status-option-${l.code}-${s}`}>{s}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <Card className="border-slate-200" data-testid="admin-translation-queue">
        <CardHeader className="pb-2">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <CardTitle className="text-base">{t("adminLang.reviewQueue")}</CardTitle>
            <Select value={queueFilter} onValueChange={setQueueFilter}>
              <SelectTrigger className="h-8 w-44 text-xs" data-testid="admin-queue-filter">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {["TO_REVIEW", "AI_TRANSLATED", "VERIFIED", "ACTIVE", "DEPRECATED", "DRAFT"].map((s) => (
                  <SelectItem key={s} value={s}>{s}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardHeader>
        <CardContent>
          {queueItems.length === 0 && (
            <p className="text-sm text-slate-500" data-testid="admin-queue-empty">{t("adminLang.noTranslations")}</p>
          )}
          <div className="space-y-2">
            {queueItems.slice(0, 30).map((tr) => (
              <div key={tr.id} className="rounded-lg border border-slate-200 p-3 flex flex-wrap items-center gap-3"
                data-testid={`admin-translation-${tr.id}`}>
                <div className="min-w-0 flex-1">
                  <p className="text-xs font-semibold text-slate-800 truncate">
                    {tr.title || tr.question || tr.translated_term || tr.id}
                  </p>
                  <p className="text-[10px] text-slate-500">
                    {tr._coll} · {tr.language_code}
                  </p>
                </div>
                <Badge variant="outline" className={TR_STATUS_STYLES[tr.status]}>{tr.status}</Badge>
                <div className="flex gap-1.5">
                  {["TO_REVIEW", "AI_TRANSLATED", "DRAFT"].includes(tr.status) && (
                    <Button size="sm" variant="outline" className="h-7 text-xs"
                      onClick={() => setTranslationStatus(tr._coll, tr.id, "VERIFIED")}
                      data-testid={`admin-tr-verify-${tr.id}`}>
                      <CheckCircle2 className="w-3 h-3 mr-1" aria-hidden="true" /> {t("adminLang.markVerified")}
                    </Button>
                  )}
                  {tr.status === "VERIFIED" && (
                    <Button size="sm" className="h-7 text-xs bg-green-700 hover:bg-green-800"
                      onClick={() => setTranslationStatus(tr._coll, tr.id, "ACTIVE")}
                      data-testid={`admin-tr-activate-${tr.id}`}>
                      {t("adminLang.markActive")}
                    </Button>
                  )}
                  {tr.status !== "DEPRECATED" && (
                    <Button size="sm" variant="ghost" className="h-7 text-xs text-red-700"
                      onClick={() => setTranslationStatus(tr._coll, tr.id, "DEPRECATED")}
                      data-testid={`admin-tr-deprecate-${tr.id}`}>
                      <Archive className="w-3 h-3 mr-1" aria-hidden="true" /> {t("adminLang.deprecate")}
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card className="border-slate-200" data-testid="admin-help-analytics">
          <CardHeader className="pb-2"><CardTitle className="text-base">{t("adminLang.analytics")}</CardTitle></CardHeader>
          <CardContent className="text-xs space-y-1">
            {analytics && Object.entries(analytics.events || {}).map(([type, byLang]) => (
              <div key={type} className="flex justify-between gap-2">
                <span className="text-slate-600 font-medium">{type}</span>
                <span className="text-slate-800">
                  {Object.entries(byLang).map(([lang, n]) => `${lang}: ${n}`).join(" · ")}
                </span>
              </div>
            ))}
            {analytics && Object.keys(analytics.events || {}).length === 0 && (
              <p className="text-slate-500">—</p>
            )}
          </CardContent>
        </Card>
        <Card className="border-slate-200" data-testid="admin-help-feedback">
          <CardHeader className="pb-2"><CardTitle className="text-base">{t("adminLang.feedback")}</CardTitle></CardHeader>
          <CardContent className="text-xs space-y-1.5 max-h-56 overflow-y-auto">
            {feedback.length === 0 && <p className="text-slate-500">—</p>}
            {feedback.slice(0, 20).map((f) => (
              <div key={f.id} className="flex justify-between gap-2 border-b border-slate-100 pb-1"
                data-testid={`admin-feedback-${f.id}`}>
                <span className="text-slate-600 truncate">
                  {f.target_type} · {f.language_code} · {f.category}
                </span>
                <span className={f.rating === 1 ? "text-green-700" : "text-red-700"}>
                  {f.rating === 1 ? "👍" : "👎"}
                </span>
              </div>
            ))}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
